/**
 * formatRaise — canonical raise-amount formatter for deal cards.
 *
 * Schema authority: tracxn_deals.raise_amount_usd (USD raw float) and
 *                   tracxn_deals.raise_amount_inr (INR raw float). Either may be null.
 * Backend reference: backend/core/export/deal_screen_common.py::format_currency
 *   INR thresholds: >= 1_00_00_000 → Cr, >= 1_00_000 → L
 *   USD thresholds: >= 1_000_000_000 → B, >= 1_000_000 → M, >= 1_000 → K
 *
 * @param {{ usd: number|string|null, inr: number|string|null }} amounts
 * @returns {string|null} Formatted string or null when both values are absent/invalid.
 *
 * Examples:
 *   formatRaise({ usd: 2_000_000, inr: 150_000_000 }) => "₹15Cr / $2M"
 *   formatRaise({ usd: 2_000_000, inr: null })         => "$2M"
 *   formatRaise({ usd: null, inr: 150_000_000 })       => "₹15Cr"
 *   formatRaise({ usd: null, inr: null })               => null
 */
export function formatRaise({ usd, inr }) {
    const usdNum = _parse(usd);
    const inrNum = _parse(inr);

    const usdStr = usdNum !== null ? _formatUsd(usdNum) : null;
    const inrStr = inrNum !== null ? _formatInr(inrNum) : null;

    if (inrStr && usdStr) return `${inrStr} / ${usdStr}`;
    if (inrStr) return inrStr;
    if (usdStr) return usdStr;
    return null;
}

// ── Private helpers ──────────────────────────────────────────────────────────

/**
 * Parse a value to a positive finite float, or return null.
 * Accepts number or numeric string. Returns null for null, undefined, 0,
 * negative values, and NaN.
 */
function _parse(value) {
    if (value == null) return null;
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (!isFinite(num) || isNaN(num) || num <= 0) return null;
    return num;
}

/**
 * Format a positive USD amount.
 * Thresholds: B >= 1e9, M >= 1e6, K >= 1e3, else toLocaleString.
 */
function _formatUsd(num) {
    if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(1)}B`;
    if (num >= 1_000_000)     return `$${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000)         return `$${(num / 1_000).toFixed(0)}K`;
    return `$${num.toLocaleString()}`;
}

/**
 * Format a positive INR amount.
 * Thresholds match backend/core/export/deal_screen_common.py::format_currency:
 *   >= 1,00,00,000 (1e7) → Cr  (the backend uses 1_00_00_000 which equals 10_000_000)
 *   >= 1,00,000 (1e5)    → L
 *   else toLocaleString.
 */
function _formatInr(num) {
    // 1_00_00_000 = 10_000_000 = 1 Crore
    if (num >= 10_000_000) return `₹${(num / 10_000_000).toFixed(1)}Cr`;
    // 1_00_000 = 100_000 = 1 Lakh
    if (num >= 100_000)    return `₹${(num / 100_000).toFixed(1)}L`;
    return `₹${num.toLocaleString()}`;
}
